// §10.2 — dragging content between windows.
//
// "The shell tracks the drag on its overlay (**apps cannot see each other's DOM**), highlights
// valid targets from their manifest `accepts`, and delivers on drop."
//
// That parenthesis is the whole reason this exists. HTML5 drag-and-drop is per-document: a drag
// begun in one iframe raises no event in another, and a drag begun in the shell cannot be dropped
// into a frame. So the shell tracks the pointer itself, decides what is under it, and delivers —
// which also means **only a shell surface can start a drag**. §9.3's `transfer` row carries no
// drag message, and an app that could start one could paint a fake drop target over a real window.
//
// This module holds the state and the accept rules. The pointer and the hit-testing are the host
// layer's, because the kernel may not touch the DOM (§6.5).
import { acceptsPayload, type AcceptRule, type Payload, type Rect } from '@hangar/shared';

export interface DragState {
  payload: Payload;
  /** What the drag looks like while it is in flight. One line, never the content. */
  label: string;
  /**
   * The window the drag started in, and its rect.
   *
   * The shell's overlay is punched out around it: a drag *inside* the source window is that app's
   * own business — Explorer dropping a file into one of its folders — and only the part of the
   * gesture that leaves the window is §10.2's.
   */
  sourceWinId: string | null;
  sourceRect: Rect | null;
  /** The window the pointer is over, or `null` for the desktop. */
  overWinId: string | null;
  /** Whether the window under the pointer would take it. `false` when there is no window. */
  accepted: boolean;
}

export interface DragService {
  state(): DragState | null;
  active(): boolean;
  /** A shell surface starts a drag. Returns false when a drag is already in flight. */
  begin(payload: Payload, label: string, source?: { winId: string; rect: Rect }): boolean;
  /** The host says which window the pointer is over. Returns whether that window would take it. */
  hover(winId: string | null): boolean;
  /**
   * Finish. Delivers to the hovered window when it accepts, and returns the window id it went to.
   * A drop on a window that does not accept, or on the desktop, delivers nothing.
   */
  drop(): string | null;
  cancel(): void;
  subscribe(onChange: () => void): () => void;
}

export interface DragOptions {
  /** What each open window is, so a hover can be resolved to an app. */
  windowAt: (winId: string) => { appId: string; instanceKey: string } | undefined;
  /** §10.2 — what that app declared it accepts. `undefined` means it accepts nothing. */
  acceptsFor: (appId: string) => readonly AcceptRule[] | undefined;
  /** §10 — hand it over, minting the payload-scoped token. Returns whether it landed. */
  deliver: (instanceKey: string, payload: Payload) => boolean;
}

export function createDragService(options: DragOptions): DragService {
  const listeners = new Set<() => void>();
  let current: DragState | null = null;

  const announce = (): void => {
    for (const listener of [...listeners]) listener();
  };

  function wouldAccept(winId: string | null, payload: Payload): boolean {
    if (winId === null) return false;
    const win = options.windowAt(winId);
    if (!win) return false;
    return acceptsPayload(options.acceptsFor(win.appId), payload);
  }

  return {
    state: () => (current ? { ...current } : null),
    active: () => current !== null,

    begin(payload, label, source) {
      if (current) return false;
      if (payload.items.length === 0) return false;
      current = {
        payload,
        label,
        sourceWinId: source?.winId ?? null,
        sourceRect: source ? { ...source.rect } : null,
        overWinId: null,
        accepted: false,
      };
      announce();
      return true;
    },

    hover(winId) {
      if (!current) return false;
      const accepted = wouldAccept(winId, current.payload);
      if (current.overWinId === winId && current.accepted === accepted) return accepted;
      current = { ...current, overWinId: winId, accepted };
      announce();
      return accepted;
    },

    drop() {
      if (!current) return null;
      const { overWinId, accepted, payload } = current;
      current = null;
      announce();
      // A drop on something that does not accept is a *cancel*, not a silent failure the user
      // has to infer: nothing was highlighted, so nothing was promised.
      if (!accepted || overWinId === null) return null;
      const win = options.windowAt(overWinId);
      if (!win) return null;
      return options.deliver(win.instanceKey, payload) ? overWinId : null;
    },

    cancel() {
      if (!current) return;
      current = null;
      announce();
    },

    subscribe(onChange) {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
  };
}
